/**
 * The digitisation queue's data, shared by the queue page and an upload's page (Rx-12): the same
 * query, so opening an upload reuses the rows already loaded, and its Previous / Next walk them.
 */

import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";

import { listDigitiseQueue, type UploadQueueQuery } from "../../api/pharmacist";

export const QUEUE_PATH = "/staff/pharmacist/uploads";

const withParams = (path: string, params: URLSearchParams) => {
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

/** The queue, with the filters it was left on (carried in the search params). */
export const queuePath = (params: URLSearchParams): string => withParams(QUEUE_PATH, params);

/** An upload's page, carrying the queue's filters so it can go back to them and walk them. */
export const uploadPath = (id: string, params: URLSearchParams): string => withParams(`${QUEUE_PATH}/${id}`, params);

/** The queue's rows for a query, a page at a time; null (a backwards date range) loads nothing. */
export function useUploadQueue(query: Omit<UploadQueueQuery, "page"> | null) {
  return useInfiniteQuery({
    queryKey: ["ph-uploads", query],
    queryFn: ({ pageParam, signal }) => listDigitiseQueue({ ...query, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
    enabled: query !== null,
    // Keep showing the current rows while a new search / filter loads, instead of a blank list.
    placeholderData: keepPreviousData,
  });
}
